import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Check, ChevronDown, X } from "lucide-react";
import { useFormStore } from "../../store/formStore";
import { labelClass } from "./fieldStyles";
import type { IntakeFormData } from "../../types/form";
import type { useStepValidation } from "../../hooks/useStepValidation";

type StringArrayField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends string[] ? K : never;
}[keyof IntakeFormData];

type Option = string | { value: string; label: string };

function normalize(opt: Option): { value: string; label: string } {
  return typeof opt === "string" ? { value: opt, label: opt } : opt;
}

interface MultiSelectInputProps {
  field: StringArrayField;
  label: string;
  validation: ReturnType<typeof useStepValidation>;
  options: Option[];
  required?: boolean;
  placeholder?: string;
  hint?: string;
  onChange?: (value: string[]) => void;
}

export function MultiSelectInput({
  field,
  label,
  validation,
  options,
  required,
  placeholder = "Select all that apply...",
  hint,
  onChange,
}: MultiSelectInputProps) {
  const value = useFormStore((s) => s.data[field]) as string[];
  const setField = useFormStore((s) => s.setField);
  const error = validation.getFieldError(field);

  const normalized = options.map(normalize);

  const deselect = (v: string) => {
    const next = value.filter((x) => x !== v);
    setField(field, next as IntakeFormData[typeof field]);
    validation.touchField(field);
    onChange?.(next);
  };

  const hasError = !!error;
  const isPlaceholder = value.length === 0;

  const displayLabel =
    value.length === 0
      ? placeholder
      : value.length === 1
      ? (normalized.find((o) => o.value === value[0])?.label ?? value[0])
      : `${value.length} selected`;

  const buttonClass = `w-full rounded-lg border pl-3 pr-8 py-2.5 text-sm text-left cursor-pointer
    focus:outline-none focus:ring-2 focus:ring-brand-green/50 focus:border-brand-green
    ${hasError ? "border-danger bg-danger-bg" : "border-gray-300 bg-white"}
    ${isPlaceholder ? "text-gray-400" : "text-gray-900"}`;

  return (
    <div>
      <label className={labelClass()}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </label>
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}

      <Listbox
        value={value}
        onChange={(val: string[]) => {
          setField(field, val as IntakeFormData[typeof field]);
          validation.touchField(field);
          onChange?.(val);
        }}
        multiple
      >
        <div className="relative">
          <ListboxButton className={buttonClass}>
            <span className="block truncate">{displayLabel}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronDown size={16} className="text-gray-400" />
            </span>
          </ListboxButton>

          <ListboxOptions
            anchor="bottom start"
            className="z-50 max-h-60 w-[var(--button-width)] overflow-auto rounded-lg bg-white py-1 text-sm
              shadow-lg ring-1 ring-black/5 focus:outline-none [--anchor-gap:4px] [--anchor-max-height:50vh]"
            transition
          >
            {normalized.map((opt) => (
              <ListboxOption
                key={opt.value}
                value={opt.value}
                className="group relative cursor-pointer select-none py-2.5 pl-10 pr-4
                  text-gray-900 data-[focus]:bg-brand-green/10 data-[focus]:text-brand-green"
              >
                {({ selected: isSelected }) => (
                  <>
                    {isSelected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-green">
                        <Check size={16} />
                      </span>
                    )}
                    <span className={`block truncate ${isSelected ? "font-semibold" : "font-normal"}`}>
                      {opt.label}
                    </span>
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((v) => {
            const optLabel = normalized.find((o) => o.value === v)?.label ?? v;
            return (
              <span
                key={v}
                className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium
                  bg-brand-green-light text-brand-green-dark border border-brand-green/30"
              >
                {optLabel}
                <button
                  type="button"
                  onClick={() => deselect(v)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-brand-green/20 transition-colors flex items-center"
                  aria-label={`Remove ${optLabel}`}
                >
                  <X size={10} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {error && <p className="text-xs text-danger mt-1.5">{error}</p>}
    </div>
  );
}
